// src/services/razorpay.js
// ─────────────────────────────────────────────────────────────
// Razorpay helper service
// Loads Razorpay script dynamically and creates orders via
// our Supabase Edge Function (secret key stays server-side)
// ─────────────────────────────────────────────────────────────

import { supabase } from './supabaseClient';

/**
 * Dynamically load the Razorpay checkout script
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Create a Razorpay order via Supabase Edge Function
 * @param {number} amount - amount in rupees (e.g. 600)
 * @param {string} receipt - unique receipt string
 * @param {object} notes  - extra info to store with order
 */
export const createRazorpayOrder = async (amount, receipt, notes = {}) => {
  // Razorpay receipt must be max 40 characters
  const safeReceipt = receipt.slice(0, 40);

  const { data, error } = await supabase.functions.invoke('razorpay-order', {
    body: {
      amount:   amount * 100, // convert ₹ to paise
      currency: 'INR',
      receipt:  safeReceipt,
      notes,
    },
  });
  if (error) throw new Error(error.message);
  if (data.error) throw new Error(data.error);
  return data; // { order, key }
};

/**
 * Save payment record to Supabase payments table
 */
export const savePayment = async (payload) => {
  const { error } = await supabase.from('payments').insert([payload]);
  if (error) console.error('Failed to save payment:', error);
};

/**
 * Open Razorpay payment popup
 * @param {object} config - { amount, name, description, receipt, notes, onSuccess, onFailure }
 */
export const openRazorpayCheckout = async ({
  amount,
  name,
  description,
  receipt,
  notes = {},
  prefill = {},
  onSuccess,
  onFailure,
}) => {
  // 1. Load script
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    onFailure('Failed to load Razorpay. Please check your internet connection.');
    return;
  }

  // 2. Create order on server
  let orderData;
  try {
    orderData = await createRazorpayOrder(amount, receipt, notes);
  } catch (err) {
    onFailure(err.message);
    return;
  }

  const { order, key } = orderData;

  // 3. Open checkout popup
  const options = {
    key,
    amount:      order.amount,
    currency:    order.currency,
    name:        'BalconyGreen 🌿',
    description,
    order_id:    order.id,
    prefill,
    theme:       { color: '#2e7d32' },
    handler: async (response) => {
      // Payment successful
      await savePayment({
        order_id:   order.id,
        payment_id: response.razorpay_payment_id,
        amount:     order.amount,
        currency:   order.currency,
        status:     'paid',
        type:       notes.type || 'general',
        notes,
      });
      onSuccess(response, order);
    },
    modal: {
      ondismiss: () => {
        onFailure('Payment cancelled by user.');
      },
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', async (response) => {
    await savePayment({
      order_id: order.id,
      amount:   order.amount,
      currency: order.currency,
      status:   'failed',
      type:     notes.type || 'general',
      notes,
    });
    onFailure(response.error?.description || 'Payment failed.');
  });

  rzp.open();
};
